"use client";

import { useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/lib/i18n/navigation";
import { Logo } from "@/components/ui/Logo";
import { LocaleSwitcher } from "@/components/ui/LocaleSwitcher";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

type Section = {
  label: string;
  links: { href: string; icon?: string; label: string; description?: string; badge?: string }[];
};

export function MobileMenu({ sections }: { sections: Section[] }) {
  const t = useTranslations("nav.mobileMenu");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(sections[0]?.label ?? null);
  const pathname = usePathname();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const toggleSection = useCallback(
    (label: string) => setExpanded((v) => (v === label ? null : label)),
    [],
  );

  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("mobile-menu-open");
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove("mobile-menu-open");
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Quick actions toujours visibles en haut
  const quickActions = [
    { href: "/recherche", icon: "🔍", label: t("quickSearch"), accent: "primary" },
    { href: "/urgences", icon: "🚨", label: t("quickEmergency"), accent: "red" },
    { href: "/pharmacie-de-garde", icon: "💊", label: t("quickPharmacy"), accent: "accent" },
    { href: "/live", icon: "📹", label: t("quickTeleconsult"), accent: "primary" },
  ];

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={open ? t("closeAria") : t("openAria")}
        aria-expanded={open}
        aria-controls="mobile-menu-drawer"
        className="relative z-[var(--z-modal)] inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--sahha-text-900)] hover:bg-[var(--sahha-surface-1)] active:bg-[var(--sahha-surface-2)] lg:hidden"
      >
        <span className="relative flex h-4 w-5 flex-col justify-between" aria-hidden>
          <span className={cn("block h-0.5 w-5 rounded-full bg-current transition-all duration-300", open ? "translate-y-[7px] rotate-45" : "")} />
          <span className={cn("block h-0.5 w-5 rounded-full bg-current transition-opacity duration-200", open ? "opacity-0" : "opacity-100")} />
          <span className={cn("block h-0.5 w-5 rounded-full bg-current transition-all duration-300", open ? "-translate-y-[7px] -rotate-45" : "")} />
        </span>
      </button>

      <div
        onClick={close}
        aria-hidden
        className={cn(
          "fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden",
          open ? "z-[var(--z-modal-backdrop)] opacity-100" : "pointer-events-none -z-10 opacity-0",
        )}
      />

      <aside
        id="mobile-menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t("drawerAria")}
        className={cn(
          "fixed inset-y-0 right-0 z-[var(--z-modal)] flex h-[100dvh] w-[min(440px,92vw)] flex-col bg-white shadow-2xl transition-transform duration-300 ease-[var(--ease-out)] lg:hidden",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between border-b border-[var(--sahha-border-subtle)] px-5 py-4">
          <Link href="/" onClick={close} aria-label={t("logoAria")}><Logo size="md" /></Link>
          <button
            type="button"
            onClick={close}
            aria-label={t("closeAria")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--sahha-text-700)] hover:bg-[var(--sahha-surface-1)]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {/* Search shortcut */}
        <div className="shrink-0 border-b border-[var(--sahha-border-subtle)] bg-[var(--sahha-surface-warm)] p-4">
          <Link
            href="/recherche"
            onClick={close}
            className="flex w-full items-center gap-3 rounded-full border border-[var(--sahha-border-default)] bg-white px-4 py-3 text-sm text-[var(--sahha-text-500)] shadow-sm transition-colors hover:border-[var(--sahha-primary-300)]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-[var(--sahha-primary-600)]" fill="none" aria-hidden>
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="flex-1">{t("searchPlaceholder")}</span>
          </Link>

          {/* Quick actions grid */}
          <div className="mt-3 grid grid-cols-4 gap-2">
            {quickActions.map((qa) => (
              <Link
                key={qa.href}
                href={qa.href as never}
                onClick={close}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 rounded-2xl border border-[var(--sahha-border-default)] bg-white py-3 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
                  qa.accent === "red" && "hover:border-red-200 hover:bg-red-50",
                  qa.accent === "accent" && "hover:border-[var(--sahha-accent-300)] hover:bg-[var(--sahha-accent-50)]",
                  qa.accent === "primary" && "hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]",
                )}
              >
                <span aria-hidden className="text-xl">{qa.icon}</span>
                <span className="text-[10px] font-semibold text-[var(--sahha-text-700)]">{qa.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Sections accordéon */}
        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-2" aria-label={t("primaryNavAria")}>
          {sections.map((section) => {
            const isOpen = expanded === section.label;
            return (
              <div key={section.label} className="border-b border-[var(--sahha-border-subtle)] last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-3 py-4 text-left"
                >
                  <span className="text-[15px] font-semibold text-[var(--sahha-text-900)]">{section.label}</span>
                  <svg
                    viewBox="0 0 20 20"
                    className={cn("h-5 w-5 text-[var(--sahha-text-500)] transition-transform", isOpen && "rotate-180")}
                    fill="none"
                    aria-hidden
                  >
                    <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className={cn("overflow-hidden transition-all duration-300", isOpen ? "max-h-[1200px] pb-2" : "max-h-0")}>
                  <ul className="space-y-0.5">
                    {section.links.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href as never}
                          onClick={close}
                          className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-[var(--sahha-primary-50)]"
                        >
                          <span aria-hidden className="text-xl leading-none pt-0.5">{link.icon}</span>
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-1.5">
                              <span className="block text-sm font-medium text-[var(--sahha-text-900)] group-hover:text-[var(--sahha-primary-700)]">
                                {link.label}
                              </span>
                              {link.badge && (
                                <span className="rounded-full bg-[var(--sahha-accent-100)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[var(--sahha-accent-700)]">
                                  {link.badge}
                                </span>
                              )}
                            </span>
                            {link.description && (
                              <span className="mt-0.5 block text-xs text-[var(--sahha-text-500)]">{link.description}</span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <footer className="shrink-0 border-t border-[var(--sahha-border-subtle)] bg-[var(--sahha-surface-1)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-medium text-[var(--sahha-text-600)]">{t("languageLabel")}</span>
            <LocaleSwitcher variant="default" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Link href="/connexion" onClick={close}>
              <Button variant="outline" block>{t("loginCta")}</Button>
            </Link>
            <Link href="/inscription" onClick={close}>
              <Button variant="primary" block>{t("signupCta")}</Button>
            </Link>
          </div>
          <a
            href="https://pro.sahha.ma"
            onClick={close}
            className="mt-3 block rounded-xl bg-gradient-to-r from-[var(--sahha-primary-50)] to-[var(--sahha-accent-50)] px-4 py-3 text-center text-xs font-semibold text-[var(--sahha-primary-700)]"
          >
            {t("proCta")}
          </a>
        </footer>
      </aside>
    </>
  );
}
